/**
 * Serialize configurable data for API requests and responses
 * Handles data transformation between frontend and backend formats
 */
class ConfigurableSerializer {
  /**
   * Transform backend API response to frontend format
   * @param {Object} apiData - Backend API response data
   * @returns {Object|null} Frontend-formatted configurable object
   */
  fromJSON(apiData) {
    const configurable = apiData?.data || apiData;
    if (!configurable) {
      return null;
    }

    const frontendConfigurable = {
      id: configurable.id,
      name: configurable.name || "",
      value: configurable.value || "",
      description: configurable.description || "",
      isActive: configurable.is_active !== false,
      createdBy: configurable.created_by || "",
      updatedBy: configurable.updated_by || "",
      createdAt: configurable.created_at
        ? this._formatDateForFrontend(configurable.created_at)
        : null,
      updatedAt: configurable.updated_at
        ? this._formatDateForFrontend(configurable.updated_at)
        : null,

      // Keep backend format for API compatibility
      is_active: configurable.is_active !== false,
      created_by: configurable.created_by || "",
      updated_by: configurable.updated_by || "",
      created_at: configurable.created_at,
      updated_at: configurable.updated_at,

      // Metadata for tracking
      metadata: {
        created_at: configurable.created_at,
        updated_at: configurable.updated_at,
        version_id: configurable.version_id,
        last_modified_by: configurable.updated_by || configurable.created_by,
      },
    };

    return frontendConfigurable;
  }

  /**
   * Transform frontend data to backend API format
   * @param {Object} frontendData - Frontend configurable object
   * @returns {Object} Backend-formatted object
   */
  toJSON(frontendData) {
    if (!frontendData) {
      return {};
    }

    const backendConfigurable = {
      name: frontendData.name || "",
      value: frontendData.value || "",
      description: frontendData.description || "",
      is_active:
        frontendData.isActive !== false && frontendData.is_active !== false,
      created_by: frontendData.createdBy || frontendData.created_by,
      updated_by: frontendData.updatedBy || frontendData.updated_by,
    };

    // Remove null/undefined values to prevent backend issues
    Object.keys(backendConfigurable).forEach((key) => {
      if (
        backendConfigurable[key] === null ||
        backendConfigurable[key] === undefined
      ) {
        delete backendConfigurable[key];
      }
    });

    return backendConfigurable;
  }

  /**
   * Transform data for create operations
   * @param {Object} frontendData - Frontend configurable data
   * @returns {Object} Backend-formatted create payload
   */
  toCreateJSON(frontendData) {
    const basePayload = this.toJSON(frontendData);

    // Ensure required fields for creation
    return {
      name: basePayload.name,
      value: basePayload.value || "",
      description: basePayload.description || "",
      is_active: basePayload.is_active !== false,
      created_by: basePayload.created_by,
    };
  }

  /**
   * Transform data for update operations
   * @param {Object} frontendData - Frontend configurable data
   * @returns {Object} Backend-formatted update payload
   */
  toUpdateJSON(frontendData) {
    const basePayload = this.toJSON(frontendData);

    // Include updated_by for tracking
    return {
      ...basePayload,
      updated_by: basePayload.updated_by,
    };
  }

  /**
   * Transform data for value-only updates
   * @param {*} value - New configuration value
   * @returns {Object} Backend-formatted value update payload
   */
  toValueUpdateJSON(value) {
    return {
      value: value,
    };
  }

  /**
   * Transform bulk update data
   * @param {Array} updates - Array of update objects with id and value
   * @returns {Object} Backend-formatted bulk update payload
   */
  toBulkUpdateJSON(updates) {
    return {
      updates: updates.map((update) => ({
        id: update.id,
        name: update.name,
        value: update.value,
      })),
    };
  }

  /**
   * Transform search response format
   * @param {Object} searchResponse - Backend search response
   * @returns {Object} Formatted search results with hits and total
   */
  fromSearchJSON(searchResponse) {
    if (!searchResponse || !searchResponse.data) {
      return { hits: [], total: 0 };
    }

    const data = searchResponse.data;

    // Handle different response formats
    if (Array.isArray(data)) {
      return {
        hits: data.map((item) => this.fromJSON(item)),
        total: data.length,
      };
    }

    if (data.hits && Array.isArray(data.hits)) {
      return {
        hits: data.hits.map((item) => this.fromJSON(item)),
        total: data.total || data.hits.length,
      };
    }

    if (data.data && Array.isArray(data.data)) {
      return {
        hits: data.data.map((item) => this.fromJSON(item)),
        total: data.total || data.data.length,
      };
    }

    // Handle pagination response format
    if (data.items && Array.isArray(data.items)) {
      return {
        hits: data.items.map((item) => this.fromJSON(item)),
        total: data.pagination?.total || data.items.length,
        pagination: data.pagination,
      };
    }

    return { hits: [], total: 0 };
  }

  /**
   * Transform bulk operation results
   * @param {Object} bulkResponse - Backend bulk operation response
   * @returns {Object} Formatted bulk results
   */
  fromBulkJSON(bulkResponse) {
    if (!bulkResponse || !bulkResponse.data) {
      return {
        summary: { total: 0, success: 0, errors: 0 },
        successItems: [],
        errorItems: [],
      };
    }

    const data = bulkResponse.data;

    return {
      summary: data.summary || { total: 0, success: 0, errors: 0 },
      successItems: data.success_items || data.successItems || [],
      errorItems: data.error_items || data.errorItems || [],
    };
  }

  /**
   * Transform minimal data for lists (performance optimization)
   * @param {Object} apiData - Backend API response data
   * @returns {Object} Minimal frontend object
   */
  fromMinimalJSON(apiData) {
    const configurable = apiData?.data || apiData;
    if (!configurable) {
      return null;
    }

    return {
      id: configurable.id,
      name: configurable.name || "",
      value: configurable.value || "",
      isActive: configurable.is_active !== false,
      updatedAt: configurable.updated_at
        ? this._formatDateForFrontend(configurable.updated_at)
        : null,
    };
  }

  /**
   * Transform value-only response
   * @param {Object} apiData - Backend value response
   * @returns {Object} Value response with metadata
   */
  fromValueJSON(apiData) {
    const data = apiData?.data || apiData;
    if (!data) {
      return null;
    }

    return {
      id: data.id,
      name: data.name || "",
      value: data.value,
      success: data.success !== false,
      message: data.message || "",
      updatedAt: data.updated_at
        ? this._formatDateForFrontend(data.updated_at)
        : null,
    };
  }

  /**
   * Create quick configuration data structure
   * @param {string} name - Configuration name
   * @param {*} value - Configuration value
   * @param {string} description - Optional description
   * @returns {Object} Quick config data structure
   */
  createQuickConfigData(name, value, description = "") {
    return {
      name: name,
      value: value,
      description: description || `Auto-generated configuration for ${name}`,
      isActive: true,
      is_active: true,
    };
  }

  /**
   * Validate configuration value based on type/format
   * @param {Object} configurable - Configuration object
   * @param {*} value - Value to validate
   * @returns {Object} Validation result
   */
  validateConfigValue(configurable, value) {
    const validation = {
      isValid: true,
      errors: [],
      warnings: [],
    };

    // Basic validation rules
    if (value === null || value === undefined) {
      validation.isValid = false;
      validation.errors.push("Value cannot be null or undefined");
      return validation;
    }

    // Check if value is too long (basic string length check)
    if (typeof value === "string" && value.length > 1000) {
      validation.warnings.push(
        "Value is quite long, consider if this is intentional"
      );
    }

    // Check for special characters that might cause issues
    if (typeof value === "string" && value.includes("<script")) {
      validation.isValid = false;
      validation.errors.push(
        "Script tags are not allowed in configuration values"
      );
    }

    return validation;
  }

  /**
   * Export configurations to JSON format
   * @param {Array} configurables - Array of configurable objects
   * @returns {Object} Export data structure
   */
  exportToJSON(configurables) {
    const exportData = {
      metadata: {
        exportedAt: new Date().toISOString(),
        totalCount: configurables.length,
        version: "1.0",
      },
      configurations: configurables.map((config) => ({
        name: config.name,
        value: config.value,
        description: config.description,
        isActive: config.isActive || config.is_active,
        updatedAt: config.updatedAt || config.updated_at,
      })),
    };

    return exportData;
  }

  /**
   * Convert date string from backend format to frontend format
   * @param {string} backendDate - Backend date string
   * @returns {string} Frontend-formatted date string
   */
  _formatDateForFrontend(backendDate) {
    if (!backendDate) return "";

    try {
      const date = new Date(backendDate);
      if (isNaN(date.getTime())) {
        return "";
      }
      return date.toISOString();
    } catch (error) {
      console.warn("Date formatting error:", error);
      return "";
    }
  }

  /**
   * Convert date string from frontend format to backend format
   * @param {string} frontendDate - Frontend date string
   * @returns {string|null} Backend-formatted date string
   */
  _formatDateForBackend(frontendDate) {
    if (!frontendDate) return null;

    try {
      const date = new Date(frontendDate);
      if (isNaN(date.getTime())) {
        return null;
      }
      return date.toISOString();
    } catch (error) {
      console.warn("Date formatting error:", error);
      return null;
    }
  }

  /**
   * Safely parse JSON value from string
   * @param {string} jsonString - JSON string to parse
   * @returns {*} Parsed value or original string if not valid JSON
   */
  _safeJSONParse(jsonString) {
    if (typeof jsonString !== "string") {
      return jsonString;
    }

    try {
      return JSON.parse(jsonString);
    } catch (error) {
      // Return original string if not valid JSON
      return jsonString;
    }
  }

  /**
   * Convert value to string for storage
   * @param {*} value - Value to stringify
   * @returns {string} String representation of value
   */
  _stringifyValue(value) {
    if (typeof value === "string") {
      return value;
    }

    if (typeof value === "object") {
      try {
        return JSON.stringify(value);
      } catch (error) {
        console.warn("JSON stringify error:", error);
        return String(value);
      }
    }

    return String(value);
  }
}

// Create and export singleton instance
export const serializer = new ConfigurableSerializer();
export default ConfigurableSerializer;
